import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Typography} from '@mui/material';
import {API_ENDPOINTS} from '../apiConfig';
import API from '../utils/API';
import Error from './Error';
import Forms from './Forms';
import {useTranslation} from 'react-i18next';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const UserModal = ({close, onUserUpdate, user}) => {
    const {t} = useTranslation();
    const inputRef = useRef(null);

    const isEdit = Boolean(user?._id);

    const [name, setName] = useState(user?.name || '');
    const [surname, setSurname] = useState(user?.surname || '');
    const [email, setEmail] = useState(user?.email || '');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [profileList, setProfileList] = useState(() =>
        (user?.profileList || []).map(p => ({value: p, label: p}))
    );
    const [sendResetLink, setSendResetLink] = useState(!isEdit);
    const [error, setError] = useState('');

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 1);
    }, []);

    useEffect(() => {
        setName(user?.name || '');
        setSurname(user?.surname || '');
        setEmail(user?.email || '');
        setProfileList((user?.profileList || []).map(p => ({value: p, label: p})));
        setPassword('');
        setPasswordConfirmation('');
        setSendResetLink(!Boolean(user?._id));
    }, [user]);

    const errors = useMemo(() => {
        const e = {};
        if (!name.trim()) e.name = t('validation.required');
        if (!surname.trim()) e.surname = t('validation.required');
        if (!email.trim() || !emailRegex.test(email)) e.email = t('validation.invalidEmail');

        if (!isEdit) {
            if (!sendResetLink) {
                if (!password) e.password = t('validation.required');
                if (password !== passwordConfirmation) e.passwordConfirmation = t('validation.passwordMismatch');
            }
        } else {
            if (password || passwordConfirmation) {
                if (password !== passwordConfirmation) e.passwordConfirmation = t('validation.passwordMismatch');
            }
        }

        return e;
    }, [name, surname, email, password, passwordConfirmation, isEdit, sendResetLink, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (Object.keys(errors).length) return;

        const profiles = (profileList || []).map(p => p.value || p);

        const payload = {
            name: name.trim(),
            surname: surname.trim(),
            email: email.trim(),
            profileList: profiles
        };

        if (!isEdit) {
            if (sendResetLink) {
                payload.sendResetLink = true;
            } else {
                payload.password = password;
                payload.passwordConfirmation = passwordConfirmation;
            }
        } else {
            if (password) {
                payload.password = password;
                payload.passwordConfirmation = passwordConfirmation;
            }
        }


        try {
            if (isEdit) {
                await API.put(API_ENDPOINTS.user.update(user._id), payload);
            } else {
                await API.post(API_ENDPOINTS.user.create, payload);
            }
            onUserUpdate?.();
            close();
        } catch (err) {
            setError(err);
            console.error('Failed to upsert user:', err);
        }
    };

    const profileOptions = [
        {value: 'Admin', label: 'Admin'},
    ];

    return (
        <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                <Forms.Text
                    ref={inputRef}
                    required
                    label={t('name')}
                    placeholder={t('name')}
                    value={name}
                    onChange={setName}
                    error={errors.name}
                    className="rounded-md"
                />
                <Forms.Text
                    required
                    label={t('surname')}
                    placeholder={t('surname')}
                    value={surname}
                    onChange={setSurname}
                    error={errors.surname}
                    className="rounded-md"
                />
                <Forms.Text
                    required
                    label="Email"
                    placeholder="email@example.com"
                    value={email}
                    onChange={setEmail}
                    error={errors.email}
                    className="rounded-md"
                />

                <Forms.Select
                    multiple
                    label={t('users.profiles')}
                    placeholder={t('users.profiles')}
                    value={profileList}
                    options={profileOptions}
                    onChange={setProfileList}
                    className="rounded-md col-span-2"
                />

                {!isEdit && (
                    <div className="col-span-2">
                        <Forms.Checkbox
                            name="sendResetLink"
                            label={t('users.sendResetLink')}
                            value={sendResetLink}
                            onChange={setSendResetLink}
                        />
                    </div>
                )}

                {(!isEdit && !sendResetLink) && (
                    <>
                        <Forms.Password
                            label={t('password')}
                            placeholder={t('password')}
                            value={password}
                            onChange={setPassword}
                            error={errors.password}
                            className="rounded-md"
                        />
                        <Forms.Password
                            label={t('passwordConfirmation')}
                            placeholder={t('passwordConfirmation')}
                            value={passwordConfirmation}
                            onChange={setPasswordConfirmation}
                            error={errors.passwordConfirmation}
                            className="rounded-md"
                        />
                    </>
                )}

                {isEdit && (
                    <>
                        <Forms.Password
                            label={t('password')}
                            placeholder={t('users.leaveBlankToKeep')}
                            value={password}
                            onChange={setPassword}
                            className="rounded-md"
                        />
                        <Forms.Password
                            label={t('passwordConfirmation')}
                            placeholder={t('passwordConfirmation')}
                            value={passwordConfirmation}
                            onChange={setPasswordConfirmation}
                            error={errors.passwordConfirmation}
                            className="rounded-md"
                        />
                    </>
                )}
            </div>

            {error && <Error error={error}/>}

            <Forms.Button submit label={t('save')} className="mt-4"/>
        </form>
    );
};

export default UserModal;
